interface PriceFormatterProps {
    amount: number
    locale?: string
    currency: string
}

const formatCurrency = ({ amount, locale, currency }: PriceFormatterProps): string => {
    const formatEuropean = (amount: number): string => {
        const formatted =
            new Intl.NumberFormat(locale, {
                style: "decimal",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })
                .format(amount)
                .replace(/\,/g, ";")
                .replace(/\./g, ",")
                .replace(/\;/g, ".") + ` ${currency}`
        return formatted
    }

    const formatSwiss = (amount: number): string => {
        // Format the amount using de-CH locale
        let formatted = new Intl.NumberFormat("de-CH", {
            style: "decimal",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount)

        // Replace the period with a comma for decimal separator
        formatted = formatted.replace(/\./g, ",")

        // Replace the apostrophe with a single quote for thousand separator
        formatted = formatted.replace(/’/g, "'")

        // Return the formatted string with currency symbol
        return formatted + " CHF"
    }

    const formatDanish = (amount: number): string => {
        const formatted =
            new Intl.NumberFormat(locale, {
                style: "decimal",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })
                .format(amount)
                .replace(/\./g, "")
                .replace(/\B(?=(\d{3})+(?!\d))/g, ".")
                .replace(/\,/g, ",") + ` ${currency}`
        return formatted
    }

    const formatNewZealand = (amount: number): string => {
        return (
            new Intl.NumberFormat(locale, {
                style: "decimal",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })
                .format(amount)
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",") + ` ${currency}`
        )
    }

    const formatAmerican = (amount: number): string => {
        return (
            new Intl.NumberFormat(locale, {
                style: "decimal",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })
                .format(amount)
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",") + ` ${currency}`
        )
    }

    const formatNorwegian = (amount: number): string => {
        return (
            new Intl.NumberFormat(locale, {
                style: "decimal",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })
                .format(amount)
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",") + ` ${currency}`
        )
    }

    const formatJapanese = (amount: number): string => {
        return (
            new Intl.NumberFormat(locale, {
                style: "decimal",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            })
                .format(amount)
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",") + ` ${currency}`
        )
    }

    const formatSwedish = (
        amount: number,
        locale = "sv-SE",
        currency = "SEK"
    ): string => {
        // Format the number with a standard decimal format
        const formattedAmount = new Intl.NumberFormat(locale, {
            style: "decimal",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount)

        // Concatenate the SEK currency after the formatted amount
        return `${formattedAmount} ${currency}`
    }

    const formatCurrencySpecifics = (
        formattedAmount: string,
        currency: string
    ): string => {
        switch (currency) {
            case "EUR":
                return formatEuropean(amount)
            case "CHF":
                return formatSwiss(amount)
            case "USD":
                return formatAmerican(amount)
            case "AUD":
                return formatAmerican(amount)
            case "CAD":
                return formatAmerican(amount)
            case "GBP":
                return formatAmerican(amount)
            case "JPY":
                return formatJapanese(amount)
            case "DKK":
                return formatDanish(amount)
            case "CZK":
                return formatEuropean(amount)
            case "SEK":
                return formatSwedish(amount)
            case "NOK":
                return formatNorwegian(amount)
            case "NZD":
                return formatNewZealand(amount)

            default:
                return formattedAmount + ` ${currency}`
        }
    }

    return formatCurrencySpecifics(amount.toString(), currency)
}

export default formatCurrency
